import React, { useEffect, useState } from 'react';
import { formatDate } from '../../utils/formatDate';
import { Attachment, ReplyTo } from './dmTypes';
import { InformationCircleIcon } from '@heroicons/react/24/outline';
import { onClickUrl } from '../../utils/openinNewTab';
import MediaHandler from './MediaHandler';

const DmMessage = ({
  image,
  text,
  received,
  timestamp,
  replyTo,
  attachments,
  getReplyMessage,
  isUnsupported,
}: {
  image: string;
  text: string;
  received: boolean;
  timestamp: Date;
  replyTo: ReplyTo;
  attachments?: Attachment[];
  getReplyMessage: any;
  isUnsupported: boolean | null;
}) => {
  const [expandedIndex, setExpandedIndex] = useState<number | null>(null);
  const [isReplyExpanded, setIsReplyExpanded] = useState(false);

  const toggleExpand = (index: number) => {
    setExpandedIndex(expandedIndex === index ? null : index);
  };

  const toggleReplyExpand = () => {
    setIsReplyExpanded(!isReplyExpanded);
  };

  const renderReplyTo = () => {
    if (!replyTo) return null;

    if ('mid' in replyTo) {
      const replyMessage = getReplyMessage(replyTo.mid);
      if (replyMessage) {
        return (
          <div
            className={`mb-1 p-2 rounded-lg text-xs text-white ${
              received ? 'bg-zinc-900' : 'bg-gray-700'
            }`}
          >
            <p className="font-bold">
              Replied to message: {/*replyMessage.sender*/}
            </p>
            <p className="truncate">{replyMessage.message}</p>
          </div>
        );
      }
    } else if ('story' in replyTo) {
      return (
        <div
          className={`mb-1 p-2 rounded-lg text-xs text-white ${
            received ? 'bg-zinc-900' : 'bg-gray-700'
          }`}
        >
          <p className="font-bold">Replied to story:</p>
          {/* <p className="truncate">Story ID: {replyTo.story.id}</p> */}
          {replyTo.story.url && (
            <MediaHandler
              url={replyTo.story.url}
              className={`rounded-lg cursor-pointer ${
                isReplyExpanded ? 'w-full' : 'w-28'
              }`}
              onExpand={toggleReplyExpand}
              isExpanded={isReplyExpanded}
            />
          )}
        </div>
      );
    }

    return null;
  };

  const renderAttachments = () => {
    if (!Array.isArray(attachments) || attachments.length === 0) return null;

    return attachments.map((attachment, index) => {
      const isExpanded = expandedIndex === index;

      if (attachment.type === 'video') {
        return (
          <div key={index} className="mt-2">
            <video
              src={attachment.payload.url}
              controls
              className="w-48"
              // className={`rounded-lg ${isExpanded ? "w-full" : "w-28"}`}
              // onClick={() => toggleExpand(index)}
            >
              Your browser does not support the video tag.
            </video>
          </div>
        );
      } else if (attachment.type === 'image') {
        return (
          <div key={index} className="mt-2">
            <img
              src={attachment.payload.url}
              alt={`Attachment ${index + 1}`}
              className={`rounded-lg cursor-pointer ${
                isExpanded ? 'w-full' : 'w-28'
              }`}
              onClick={() => toggleExpand(index)}
            />
          </div>
        );
      } else if (attachment.type === 'story_mention') {
        return (
          <div key={index} className="mt-2">
            <div
              className={`mb-1 p-2 rounded-lg text-xs text-white ${
                received ? 'bg-zinc-900' : 'bg-gray-700'
              }`}
            >
              <p className="font-bold mb-1">Mentioned you in their story:</p>
              <MediaHandler
                url={attachment.payload.url}
                className={`rounded-lg cursor-pointer ${
                  isExpanded ? 'w-full' : 'w-28'
                }`}
                onExpand={() => toggleExpand(index)}
                isExpanded={isExpanded}
              />
            </div>
          </div>
        );
      } else if (attachment.type === 'share') {
        return (
          <div key={index} className="mt-2">
            <div
              className={`mb-1 p-2 rounded-lg text-xs text-white ${
                received ? 'bg-zinc-900' : 'bg-gray-700'
              }`}
            >
              <p className="font-bold mb-1">Shared:</p>
              <img
                src={attachment.payload.url}
                alt={`Attachment ${index + 1}`}
                className={`rounded-lg cursor-pointer ${
                  isExpanded ? 'w-full' : 'w-28'
                }`}
                onClick={() => toggleExpand(index)}
              />
            </div>
          </div>
        );
      } else if (attachment.type === 'audio') {
        return (
          <div className="audio-message">
            <audio controls src={attachment.payload.url}>
              Your browser does not support the audio element.
            </audio>
          </div>
        );
      }
      return null;
    });
  };

  const getMessageStyle = () => {
    if (isUnsupported) {
      return 'bg-gray-800';
    }
    if (text) {
      return received ? 'bg-violet p-2' : 'bg-grey p-2';
    }
    return '';
  };

  return (
    <div className={`flex flex-col mt-4 ${received ? '' : 'items-end mr-2'}`}>
      <div className={`flex flex-row`}>
        {received && (
          <img
            src={
              image ||
              'https://superpower-alpha.s3.amazonaws.com/default_images/no_pfp.jpg'
            }
            className="w-10 h-10 rounded-full mr-2 mt-auto object-cover flex-shrink-0"
            alt="Profile"
          />
        )}
        <div className={`flex flex-col max-w-[320px]`}>
          {renderReplyTo()}
          <div
            className={`flex flex-col leading-1.5 justify-center ${getMessageStyle()} rounded-2xl`}
          >
            {!isUnsupported && text && (
              <p className="break-words whitespace-pre-line text-sm font-normal text-white">
                {text}
              </p>
            )}

            {isUnsupported && (
              <div className="flex items-center px-3 py-2 rounded-lg">
                {/* <InformationCircleIcon className="h-4 w-4 stroke-2 text-white" /> */}
                <div className="flex flex-col">
                  <p className="text-sm">Unsupported message</p>
                  {/* <button
                    onClick={onClickUrl("https://www.superpower.social/")}
                    className="text-xs text-white hover:text-gray-300 transition-colors duration-100 text-left"
                  >
                    To learn more about unsupported messages click here
                  </button> */}
                </div>
              </div>
            )}
            {renderAttachments()}
          </div>
        </div>
      </div>
      {timestamp && (
        <div
          className={`text-xs text-gray-500 mt-1 ${
            received ? 'ml-12' : 'mr-2'
          }`}
        >
          {formatDate(timestamp)}
        </div>
      )}
    </div>
  );
};

export default DmMessage;
